/**
 * useBoardDragHandlers - DnD event handlers for the board
 * Extracted from BoardV3.tsx for readability
 * Reads selection directly from selectionStore.
 */

import { useState, useCallback, useRef } from 'react';
import type { DragEndEvent, DragStartEvent, DragMoveEvent } from '@dnd-kit/core';
import type { Task } from '../../types';
import { type BoardV3DragState, createInitialDragState } from '../components/BoardV3Types';
import { getTaskDayIndex, businessDayIndexToDate } from '../../lib/dateUtils';
import { selectionStore } from '../stores/selectionStore';

interface UseBoardDragHandlersParams {
  columnYOffsets: Record<string, number>;
  rowAssignments: Record<string, Map<string, number>>;
  columnLabelWidth: number;
  dayWidth: number;
  headerHeight: number;
  rowHeight: number;
  boardStartDate?: string;
  getColumnFromY: (y: number) => string | null;
  getDayFromX: (x: number) => number;
  onTaskMove?: (taskId: string, column: string, startDate: string) => void;
  tasks?: Task[];
  onBulkMove?: (taskIds: string[], dayDelta: number, primaryTaskId: string, primaryColumn: string) => void;
}

export function useBoardDragHandlers({
  columnYOffsets,
  rowAssignments,
  columnLabelWidth,
  dayWidth,
  headerHeight,
  rowHeight,
  boardStartDate,
  getColumnFromY,
  getDayFromX,
  onTaskMove,
  tasks,
  onBulkMove,
}: UseBoardDragHandlersParams) {
  const [dragState, setDragState] = useState<BoardV3DragState>(createInitialDragState);

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current;
    if (data?.type === 'task' && data.task) {
      const task = data.task as Task;

      const taskYOffset = columnYOffsets[task.column] ?? 0;
      const taskRow = rowAssignments[task.column]?.get(task.id) ?? 0;

      const taskDayIdx = getTaskDayIndex(task.startDate, boardStartDate);
      const initialX = columnLabelWidth + taskDayIdx * dayWidth;
      const initialY = headerHeight + taskYOffset + taskRow * rowHeight;

      const selIds = selectionStore.getSnapshot().context.selectedIds;
      const selSet = new Set(selIds);
      const isBulk = selSet.has(task.id) && selIds.length > 1;

      let minSelectedDayIndex: number | null = null;
      if (isBulk && tasksRef.current) {
        minSelectedDayIndex = Infinity;
        for (const t of tasksRef.current) {
          if (selSet.has(t.id)) {
            const d = getTaskDayIndex(t.startDate, boardStartDate);
            if (d < minSelectedDayIndex) minSelectedDayIndex = d;
          }
        }
        if (minSelectedDayIndex === Infinity) minSelectedDayIndex = 0;
      }

      setDragState({
        activeTask: task,
        initialPosition: { x: initialX, y: initialY },
        snapTarget: { column: task.column, day: taskDayIdx },
        isBulkDrag: isBulk,
        bulkDragCount: isBulk ? selIds.length : 0,
        initialDayIndex: taskDayIdx,
        minSelectedDayIndex: isBulk ? minSelectedDayIndex : null,
      });
    }
  }, [columnYOffsets, rowAssignments, columnLabelWidth, dayWidth, headerHeight, rowHeight, boardStartDate]);

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    if (!dragState.activeTask || !dragState.initialPosition) return;

    const x = dragState.initialPosition.x + (event.delta?.x || 0);
    const y = dragState.initialPosition.y + (event.delta?.y || 0);

    const centerY = y + rowHeight / 2;
    const column = getColumnFromY(centerY);
    let day = getDayFromX(x);

    // For bulk drag: clamp so no task goes before day 0
    if (dragState.isBulkDrag && dragState.initialDayIndex != null && dragState.minSelectedDayIndex != null) {
      const dayDelta = day - dragState.initialDayIndex;
      const minAllowed = -dragState.minSelectedDayIndex;
      if (dayDelta < minAllowed) {
        day = dragState.initialDayIndex + minAllowed;
      }
    }

    if (column) {
      setDragState(prev => ({
        ...prev,
        snapTarget: { column, day },
      }));
    }
  }, [dragState.activeTask, dragState.initialPosition, dragState.isBulkDrag, dragState.initialDayIndex, dragState.minSelectedDayIndex, getColumnFromY, getDayFromX, rowHeight]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active } = event;
    const activeData = active.data.current;

    if (activeData?.type === 'task' && dragState.snapTarget) {
      const task = activeData.task as Task;
      const { column, day } = dragState.snapTarget;

      if (dragState.isBulkDrag && dragState.initialDayIndex != null && onBulkMove) {
        const dayDelta = day - dragState.initialDayIndex;
        const selIds = selectionStore.getSnapshot().context.selectedIds;
        if (selIds.length > 0 && (dayDelta !== 0 || task.column !== column)) {
          onBulkMove(selIds, dayDelta, task.id, column);
        }
      } else {
        const currentDayIdx = getTaskDayIndex(task.startDate, boardStartDate);
        if (task.column !== column || currentDayIdx !== day) {
          onTaskMove?.(task.id, column, businessDayIndexToDate(day, boardStartDate));
        }
      }
    }

    setDragState(createInitialDragState());
  }, [dragState.snapTarget, dragState.isBulkDrag, dragState.initialDayIndex, onTaskMove, onBulkMove, boardStartDate]);

  return { dragState, handleDragStart, handleDragMove, handleDragEnd };
}
